import React from 'react';
import { BOOKING_URL } from '../../../constants/config';
import {
  Container,
  Typography,
  Grid,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Alert, // Added to highlight myth-busting
  AlertTitle, // Added for Alert
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HealingIcon from '@mui/icons-material/Healing'; // For saving tooth/benefits
import PainIcon from '@mui/icons-material/ReportProblem'; // Placeholder for pain/symptoms - choose a better one if available
import InfoIcon from '@mui/icons-material/Info'; // For myths/info
import AssuredWorkloadIcon from '@mui/icons-material/AssuredWorkload'; // For process
import SpaIcon from '@mui/icons-material/Spa'; // For comfort / why choose us
import StarIcon from '@mui/icons-material/Star'; // For why choose us
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety'; // For benefits
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

// Consider a more specific icon if available, e.g., a tooth-with-root icon
import AccountTreeIcon from '@mui/icons-material/AccountTree'; // Placeholder for root/canal structure

const RootCanalTherapy = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const benefits = [
    'Relieves severe tooth pain caused by infection or inflammation.',
    'Saves your natural tooth, preventing the need for extraction.',
    'Stops the spread of infection to other teeth or parts of your body.',
    'Allows for restoration with a crown, enabling normal chewing function.',
    'An efficient and highly successful procedure with modern techniques.',
    'Maintains your natural smile and alignment by preserving the tooth.'
  ];

  const process = [
    {
      title: '1. Diagnosis & Anesthesia for Comfort',
      description: 'We start with a thorough examination and X-rays to confirm the need for treatment. Then, local anesthesia is administered to completely numb the tooth and surrounding area, ensuring you remain comfortable throughout.'
    },
    {
      title: '2. Isolating the Tooth & Accessing the Pulp',
      description: 'A small protective sheet (dental dam) is placed around the tooth to keep it clean and dry. A small opening is then made in the crown of the tooth to access the infected pulp chamber.'
    },
    {
      title: '3. Removing Infected Pulp & Cleaning Canals',
      description: 'Using specialized instruments, the damaged or infected pulp is carefully removed from the pulp chamber and root canals. The canals are then meticulously cleaned, shaped, and disinfected.'
    },
    {
      title: '4. Filling & Sealing the Canals',
      description: 'Once cleaned, the canals are filled with a biocompatible, rubber-like material called gutta-percha. This, along with an adhesive cement, completely seals the canals to prevent reinfection.'
    },
    {
        title: '5. Sealing the Tooth & Planning Restoration',
        description: 'A temporary or permanent filling is placed to close the access opening. As a root canal treated tooth needs protection, we will typically recommend placing a dental crown soon after to restore its full strength and function.'
    }
  ];

  const signsNeeded = [
    'Severe, persistent, or throbbing toothache.',
    'Sharp pain when biting or chewing.',
    'Prolonged sensitivity to hot or cold temperatures, even after the stimulus is removed.',
    'Discoloration or darkening of the affected tooth.',
    'Swelling and tenderness in the nearby gums.',
    'A persistent or recurring pimple (abscess) on the gums near the tooth root.'
  ];

  const aftercareInfo = [
    {
      title: 'Immediate Care',
      desc: 'Some tenderness is normal for a few days, manageable with over-the-counter pain relievers. Avoid chewing hard foods on the treated tooth until it\'s fully restored.',
      icon: <HealingIcon sx={{ color: 'primary.main' }}/>
    },
    {
      title: 'Importance of Restoration (Crown)',
      desc: 'A root canal removes the tooth\'s nerve and blood supply, making it more brittle. A dental crown is nearly always recommended afterward to protect the tooth from fracture and restore its function.',
      icon: <StarIcon sx={{ color: 'secondary.main' }}/> // Represents quality/necessity
    },
    {
      title: 'Long-Term Success',
      desc: 'With proper care (good hygiene, regular check-ups) and restoration, a root canal treated tooth can last a lifetime.',
      icon: <CheckCircleOutlineIcon sx={{ color: 'green' }}/>
    },
  ];

  const mythsFacts = [
    {
        myth: 'Root canals are extremely painful.',
        fact: 'This is the most common myth! With modern anesthetics and techniques, root canal therapy is typically no more uncomfortable than getting a filling. The procedure actually *relieves* the significant pain caused by the infection.'
    },
    {
        myth: 'It\'s better to just pull the tooth.',
        fact: 'Saving your natural tooth is almost always the best option. Tooth extraction can lead to adjacent teeth shifting, bite problems, and the need for more complex restorations like bridges or implants.'
    },
    {
        myth: 'Root canals cause illness.',
        fact: 'This is a long-debunked theory based on poorly designed research from a century ago. There is no valid scientific evidence linking root canal treatment to diseases elsewhere in the body. It is a safe and effective procedure.'
    }
  ];
  
  const faqs = [
    {
      question: 'How many appointments does a root canal take?',
      answer: 'Most root canals can be completed in one or two appointments, depending on the complexity of the case and the extent of the infection.'
    },
    {
      question: 'What happens if I don\'t get a needed root canal?',
      answer: 'The infection can worsen, leading to increased pain, a dental abscess (a pus-filled pocket), bone loss around the tooth root, and potentially spreading the infection to other areas. Ultimately, the tooth may need to be extracted.'
    },
    {
      question: 'What is the success rate of root canal therapy?',
      answer: 'Root canal treatment has a very high success rate, generally above 95%. When properly restored (usually with a crown), the treated tooth can function normally for many years, often a lifetime.'
    },
    {
        question: 'Are there alternatives to root canal therapy?',
        answer: 'The primary alternative is tooth extraction. However, this leads to a gap that may require a bridge, implant, or partial denture to restore function and prevent surrounding teeth from shifting.'
    },
    {
        question: 'Do I definitely need a crown after a root canal?',
        answer: 'In most cases, especially for back teeth that endure significant chewing forces, a crown is highly recommended to protect the tooth from fracturing. Your dentist will advise the best restoration for your specific situation.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Root Canal Therapy | Save Your Tooth | Pacifica Dental</title>
        <meta name="description" content="Relieve tooth pain and save your natural tooth with expert root canal therapy at Pacifica Dental. Learn about our comfortable, modern endodontic treatment." />
        <meta name="keywords" content="root canal therapy, endodontics, root canal treatment, save tooth, tooth pain relief, infected tooth, [Your City] dentist, endodontist" />
      </Helmet>
      
      <Box 
        sx={{ 
          py: { xs: 6, md: 8 },
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e9eef3 100%)',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant={isMobile ? "h3" : "h2"}
              component="h1"
              textAlign="center"
              gutterBottom
              sx={{ 
                mb: 2,
                fontWeight: 700,
                color: 'primary.main'
              }}
            >
              Saving Your Smile: Advanced Root Canal Therapy
            </Typography>

            <Typography
              variant={isMobile ? "h6" : "h5"}
              component="h2"
              textAlign="center"
              gutterBottom
              sx={{ mb: 4, color: 'text.secondary', fontWeight: 500 }}
            >
              Relieving Pain and Preserving Your Natural Teeth
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{ mb: 4, fontSize: '1.15rem', lineHeight: 1.8, color: 'text.primary' }}
            >
              Hearing you need a root canal might sound daunting, but at Pacifica Dental, we want to assure you that modern root canal therapy is a routine, comfortable procedure designed to **relieve pain** and **save your natural tooth**. This treatment becomes necessary when the soft inner tissue (pulp) of your tooth becomes infected or inflamed due to deep decay, trauma, or other issues. Our experienced team uses advanced techniques and anesthesia to make the process smooth and effective, preserving your smile's health and integrity.
            </Typography>
            <Divider sx={{ my: 4 }} />

            {/* Benefits Section */}
            <Grid container spacing={isMobile ? 3 : 5} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    backgroundColor: theme.palette.mode === 'light' ? 'primary.light' : 'primary.dark',
                    color: 'white',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2}}>
                    Why Root Canal Therapy is Beneficial
                  </Typography>
                  <List dense>
                    {benefits.map((benefit, index) => (
                      <ListItem key={index} sx={{ py: 0.5}}>
                        <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5}}>
                          <HealthAndSafetyIcon sx={{ color: 'white', fontSize: '1.4rem' }} />
                        </ListItemIcon>
                        <ListItemText primary={benefit} primaryTypographyProps={{fontSize: '1.05rem'}} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

              {/* Process Section */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom color="primary.main" sx={{ fontWeight: 600, mb: 2}}>
                    The Root Canal Procedure: A Closer Look
                  </Typography>
                  {process.map((step, index) => (
                    <Box key={index} sx={{ mb: index === process.length - 1 ? 0 : 2.5 }}>
                       <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                         <AssuredWorkloadIcon color="primary" sx={{ mr: 1, fontSize: '1.3rem' }} />
                        <Typography variant="h6" color="primary.dark" fontWeight="bold" sx={{fontSize: '1.1rem'}}>
                          {step.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{fontSize: '0.95rem', lineHeight: 1.6, pl: '2.1rem'}}>
                        {step.description}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 5 }} /> 

            {/* Signs You Might Need a Root Canal Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Recognizing the Signs You May Need Treatment
              </Typography>
              <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? '#fff3e0' : '#4e342e' }}> {/* Light orange/warning background */}
                <Typography variant="body1" paragraph sx={{ mb: 2, fontSize: '1.1rem', color: 'text.secondary' }}>
                  Contact us if you experience any of the following symptoms, as they could indicate the need for root canal therapy:
                </Typography>
                <Grid container spacing={1}>
                    {signsNeeded.map((item, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                            <List dense sx={{py:0}}>
                                <ListItem sx={{py:0.2}}>
                                    <ListItemIcon sx={{minWidth: 'auto', marginRight: 1, color: 'error.main'}}>
                                        <PainIcon /> {/* Using placeholder PainIcon */}
                                    </ListItemIcon>
                                    <ListItemText primary={item} primaryTypographyProps={{fontSize: '1rem'}}/>
                                </ListItem>
                            </List>
                        </Grid>
                    ))}
                </Grid>
              </Paper>
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Addressing Root Canal Myths Section */}
             <Box sx={{ my: 5 }}>
                <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                    Debunking Common Root Canal Myths
                </Typography>
                {mythsFacts.map((item, index) => (
                     <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 2, borderRadius: 1.5, p:1.5 }} elevation={2}>
                        <AlertTitle sx={{fontWeight: 'bold'}}>Myth: {item.myth}</AlertTitle>
                        <Typography variant="body2"><strong>Fact:</strong> {item.fact}</Typography>
                    </Alert>
                ))}
            </Box>

            <Divider sx={{ my: 5 }} />


            {/* What Happens After Section */}
            <Box sx={{ my: 5 }}>
                <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                    Life After Root Canal Therapy
                </Typography>
                <Grid container spacing={3} justifyContent="center">
                    {aftercareInfo.map((item, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Paper elevation={2} sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2 }}>
                                <Box sx={{display: 'flex', alignItems: 'center', mb:1}}>
                                    {item.icon}
                                    <Typography variant="h6" component="h4" sx={{fontWeight: 'bold', fontSize:'1.1rem', color: 'secondary.dark', ml:1}}>
                                        {item.title}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{flexGrow: 1, fontSize: '0.95rem'}}>
                                    {item.desc}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Divider sx={{ my: 5 }} />


            {/* FAQ Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Your Questions About Root Canal Therapy Answered
              </Typography>
              {faqs.map((faq, index) => (
                <Accordion key={index} elevation={2} sx={{ mb: 1.5, borderRadius: 1, '&:before': { display: 'none' } }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon color="primary"/>}
                    aria-controls={`faq-panel${index}-content`}
                    id={`faq-panel${index}-header`}
                    sx={{ '& .MuiAccordionSummary-content': { fontWeight: 'medium', fontSize: '1.1rem' } }}
                  >
                    {faq.question}
                  </AccordionSummary>
                  <AccordionDetails sx={{backgroundColor: theme.palette.mode === 'light' ? '#fafafa' : '#333' }}>
                    <Typography color="text.secondary" sx={{lineHeight: 1.7, fontSize: '1rem'}}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Why Choose Us Section */}
            <Box sx={{ mt: 6, p:3, background: theme.palette.mode === 'light' ? 'rgba(0, 123, 255, 0.05)' : 'rgba(100,100,100,0.1)', borderRadius:2 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb:3 }}>
                Why Trust Us With Your Root Canal Therapy?
              </Typography>
              <Grid container spacing={3}>
                  {[
                      { title: "Experienced & Gentle Care", text: "Our dentists have extensive experience performing root canals with a focus on gentle techniques and patient comfort.", icon: <SpaIcon color="primary" /> },
                      { title: "Advanced Diagnostic & Treatment Technology", text: "We utilize modern tools like digital X-rays and potentially magnification (microscopes) for precise and effective treatment.", icon: <AccountTreeIcon color="primary" /> }, // Placeholder icon
                      { title: "Commitment to Saving Natural Teeth", text: "We prioritize treatments that preserve your natural teeth, understanding their importance for long-term oral health.", icon: <HealingIcon color="primary" /> },
                      { title: "Clear Communication & Support", text: "We explain the procedure clearly, answer all your questions, and provide support throughout your treatment and recovery.", icon: <StarIcon color="primary" /> }
                  ].map(item => (
                      <Grid item xs={12} sm={6} key={item.title}>
                          <Box sx={{display: 'flex', alignItems: 'flex-start'}}>
                              <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, marginTop: 0.5}}>
                                  {item.icon}
                              </ListItemIcon>
                              <Box>
                                  <Typography variant="h6" component="h4" sx={{fontWeight: 'bold', fontSize: '1.1rem'}}>{item.title}</Typography>
                                  <Typography variant="body2" color="text.secondary">{item.text}</Typography>
                              </Box>
                          </Box>
                      </Grid>
                  ))}
              </Grid>
            </Box>


            {/* Call to Action */}
            <Box sx={{ mt: 6, py: 4, textAlign: 'center', background: theme.palette.primary.main, color: 'white', borderRadius: 2 }}>
                <Typography variant={isMobile ? "h5" : "h4"} gutterBottom sx={{fontWeight: 600}}>
                    Experiencing Severe Tooth Pain? Don't Wait!
                </Typography>
                <Typography variant="body1" sx={{mb:3, opacity: 0.9}}>
                    Timely treatment can save your tooth and alleviate discomfort. Contact us today to schedule a consultation.
                </Typography>
              <Button
                onClick={() => window.location.href = BOOKING_URL}
                variant="contained"
                color="secondary"
                size="large"
                sx={{
                  px: 5, 
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  '&:hover': {
                    backgroundColor: 'secondary.dark',
                  }
                }}
              >
                Schedule Your Root Canal Consultation
              </Button>
              <Typography variant="body2" sx={{mt: 2, opacity: 0.8}}>
                Or call us immediately at <Link to="tel:(408) 869-0998" style={{color: 'white', fontWeight: 'bold'}}>(408) 869-0998</Link> if you are in severe pain.
              </Typography>
            </Box>

          </motion.div>
        </Container>
      </Box>
    </>
  );
};

export default RootCanalTherapy;